import React, { useState } from 'react';
import { auth, db, appId } from '../utils/firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Icons } from './Icons';

const LoginGate = ({ onSuccess, onClose, setHandle, setClearance }) => {
    // The old "frequency" code step is skipped: the account and your approval are the real lock. (Set 'resonance' here to bring it back.)
    const [stage, setStage] = useState('auth');
    const [needsProfile, setNeedsProfile] = useState(false); // a new Google account that has not asked for access yet
    const [mode, setMode] = useState('login'); 
    const [resonanceCode, setResonanceCode] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [localHandle, setLocalHandle] = useState('');
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleResonance = (e) => {
        e.preventDefault();
        if (['1956', 'DOORS', 'ADMIN'].includes(resonanceCode.toUpperCase())) { 
            localStorage.setItem('ts_resonance', 'cleared'); 
            setStage('auth'); 
            setError(false); 
        } 
        else { setError('FREQUENCY MISMATCH'); }
    };
    
    const handleReset = async () => {
        let resetEmail = email || window.prompt("Enter email for password reset:");
        if (!resetEmail) return;
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            alert(`RESET SIGNAL SENT TO ${resetEmail}`);
        } catch (e) { 
            console.error(e); setError('RESET FAILED: ' + e.message); 
        }
    };

    const profileRef = (uid) => doc(db, 'artifacts', appId, 'public', 'data', 'users', uid);

    // Google sign-in. People we already approved go straight in; a new Google account asks for access and waits.
    const handleGoogle = async () => {
        setLoading(true); setError(false);
        window.__crewAuthBusy = true;
        try {
            const cred = await signInWithPopup(auth, new GoogleAuthProvider());
            const snap = await getDoc(profileRef(cred.user.uid));
            if (!snap.exists()) { setNeedsProfile(true); setLoading(false); window.__crewAuthBusy = false; return; }
            const d = snap.data();
            if (d.status === 'PENDING') { await signOut(auth); setError('ACCESS DENIED: PENDING'); setLoading(false); window.__crewAuthBusy = false; return; }
            setHandle(d.handle || 'Operative'); setClearance(d.clearance || 'LEVEL 1');
            window.__crewAuthBusy = false;
            onSuccess(d.clearance === 'FIELD OP' || d.clearance === 'LEVEL 0' ? 'unauthorized' : 'route');
        } catch (err) {
            window.__crewAuthBusy = false;
            console.error(err);
            if (!(err && (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request'))) setError('AUTH FAILED');
            setLoading(false);
        }
    };

    const requestGoogleAccess = async () => {
        if (!localHandle.trim()) { setError('HANDLE REQUIRED'); return; }
        setLoading(true); setError(false);
        window.__crewAuthBusy = true;
        try {
            await setDoc(profileRef(auth.currentUser.uid), { handle: localHandle.trim(), email: auth.currentUser.email, status: 'PENDING', clearance: 'LEVEL 1', timestamp: serverTimestamp() });
            alert("REQUEST SENT. IDENTITY PENDING ADMIN APPROVAL.");
            await signOut(auth); setNeedsProfile(false);
        } catch (err) { console.error(err); setError('REQUEST FAILED'); }
        window.__crewAuthBusy = false; setLoading(false);
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setLoading(true); setError(false);
        try {
            if (mode === 'register') {
                if (!localHandle) { setError('HANDLE REQUIRED'); setLoading(false); return; }
                const cred = await createUserWithEmailAndPassword(auth, email, password);
                await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', cred.user.uid), {
                    handle: localHandle, email: email, status: 'PENDING', clearance: 'LEVEL 1', timestamp: serverTimestamp()
                });
                alert("REGISTRATION SUCCESSFUL. IDENTITY PENDING ADMIN APPROVAL.");
                await signOut(auth); setMode('login'); setLoading(false);
            } else {
                const cred = await signInWithEmailAndPassword(auth, email, password);
                const userDoc = await getDoc(doc(db, 'artifacts', appId, 'public', 'data', 'users', cred.user.uid));
                if (userDoc.exists()) {
                    const d = userDoc.data();
                    if (d.status === 'PENDING') throw new Error("IDENTITY PENDING APPROVAL");
                    setHandle(d.handle || 'Operative');
                    setClearance(d.clearance || 'LEVEL 1');
                    onSuccess(d.clearance === 'FIELD OP' || d.clearance === 'LEVEL 0' ? 'unauthorized' : 'route');
                } else {
                    setHandle(email.split('@')[0]); 
                    setClearance('LEVEL 1');
                    onSuccess('hub');
                }
            }
        } catch (err) { 
            console.error(err); setError(err.message.includes("PENDING") ? "ACCESS DENIED: PENDING" : "AUTH FAILED"); setLoading(false); 
        }
    };

    return (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 font-sans text-white">
            <div className="w-full max-w-md bg-[#0a0a0a] border border-gray-800 p-10 shadow-2xl relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-600 hover:text-white"><Icons.X /></button>
                <div className="text-center mb-10">
                    <div className="h-16 w-16 mx-auto text-white mb-6">
                        <Icons.TriangleLogo className="w-full h-full" />
                    </div>
                    <h2 className="text-xl font-black tracking-[0.3em] uppercase">TERTIARY GATE</h2>
                    <p className="text-[10px] text-gray-500 mt-2 font-bold tracking-widest uppercase">{stage === 'resonance' ? 'Frequency Check' : 'Staff Login'}</p>
                </div>
                {stage === 'resonance' ? (
                    <form onSubmit={handleResonance} className="space-y-6">
                        <input type="password" value={resonanceCode} onChange={(e) => { setResonanceCode(e.target.value); setError(false); }} className="w-full bg-[#111] border-b-2 border-gray-800 py-3 px-2 text-white font-mono text-center focus:border-red-600 focus:outline-none text-lg tracking-widest" placeholder="####" autoFocus />
                        {error && <div className="text-red-600 text-xs font-bold text-center animate-pulse">{error}</div>}
                        <button type="submit" className="w-full bg-red-900/20 border border-red-900/50 text-red-500 py-4 font-black uppercase tracking-[0.2em] hover:bg-red-900 hover:text-white transition-all mt-4 text-xs">VERIFY FREQUENCY</button>
                    </form>
                ) : needsProfile ? (
                    <div className="space-y-6">
                        <p className="text-xs text-gray-400 text-center">New here. Pick a handle and ask for access. An admin approves new accounts by hand.</p>
                        <input type="text" value={localHandle} onChange={(e) => setLocalHandle(e.target.value)} maxLength={40} className="w-full bg-[#111] border-b-2 border-gray-800 py-2 px-2 text-white text-xs focus:border-blue-600 outline-none" placeholder="HANDLE" />
                        {error && <div className="text-red-600 text-xs font-bold text-center animate-pulse">{error}</div>}
                        <button type="button" onClick={requestGoogleAccess} disabled={loading} className="w-full bg-blue-900/20 border border-blue-900/50 text-blue-500 py-4 font-black uppercase tracking-[0.2em] hover:bg-blue-900 hover:text-white transition-all disabled:opacity-50 text-xs">REQUEST ACCESS</button>
                    </div>
                ) : (
                    <>
                    <button type="button" onClick={handleGoogle} disabled={loading} className="w-full mb-6 py-3 bg-white text-black font-bold text-xs rounded flex items-center justify-center gap-2 disabled:opacity-50 tracking-wide">
                        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/><path fill="#FBBC05" d="M10.5 28.7a14.5 14.5 0 0 1 0-9.4l-7.9-6.1a24 24 0 0 0 0 21.6l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.9 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
                        CONTINUE WITH GOOGLE
                    </button>
                    <div className="flex items-center gap-3 mb-6 text-[10px] text-gray-600"><span className="flex-1 h-px bg-gray-800" />OR EMAIL<span className="flex-1 h-px bg-gray-800" /></div>
                    <form onSubmit={handleAuth} className="space-y-6">
                        <div className="space-y-4">
                            {mode === 'register' && <input type="text" value={localHandle} onChange={(e) => setLocalHandle(e.target.value)} maxLength={40} className="w-full bg-[#111] border-b-2 border-gray-800 py-2 px-2 text-white text-xs focus:border-blue-600 outline-none" placeholder="HANDLE" />}
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#111] border-b-2 border-gray-800 py-2 px-2 text-white text-xs focus:border-blue-600 outline-none" placeholder="EMAIL" />
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#111] border-b-2 border-gray-800 py-2 px-2 text-white text-xs focus:border-blue-600 outline-none" placeholder="PASSWORD" />
                        </div>
                        {error && <div className="text-red-600 text-xs font-bold text-center animate-pulse">{error}</div>}
                        <button type="submit" disabled={loading} className="w-full bg-blue-900/20 border border-blue-900/50 text-blue-500 py-4 font-black uppercase tracking-[0.2em] hover:bg-blue-900 hover:text-white transition-all disabled:opacity-50 text-xs">{loading ? 'PROCESSING...' : (mode === 'register' ? 'SUBMIT' : 'LOGIN')}</button>
                        <div className="flex justify-between items-center pt-4 border-t border-gray-800">
                            <button type="button" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(false); }} className="text-[10px] uppercase font-bold tracking-widest text-gray-500 hover:text-white transition-colors">{mode === 'login' ? 'New Operative? Register' : 'Existing? Login'}</button>
                            {mode === 'login' && <button type="button" onClick={handleReset} className="text-[10px] uppercase font-bold tracking-widest text-red-500 hover:text-red-400 transition-colors">Forgot Password?</button>}
                        </div>
                    </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginGate;